import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useAutoSave
 * Debounces writes of arbitrary JSON data to the phase-draft endpoint so users
 * never lose progress when they close the tab, refresh, or lose connection.
 *
 * Usage:
 *   const { status, savedAt, saveNow, loadDraft, clearDraft } =
 *     useAutoSave({ sessionId, phaseNumber: 1, data: formData, enabled: !existingData });
 *
 * status: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
 * savedAt: ISO timestamp string of last successful save
 * saveNow(): force an immediate save (returns promise)
 * loadDraft(): fetch the latest draft (returns the stored data object or null)
 * clearDraft(): delete the stored draft (call after successful final submit)
 */
export const useAutoSave = ({
  sessionId,
  phaseNumber,
  data,
  enabled = true,
  debounceMs = 2500,
}) => {
  const [status, setStatus] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);
  const inFlightRef = useRef(null);
  const lastSerializedRef = useRef('');
  const firstRunRef = useRef(true);

  const doSave = useCallback(async (payload) => {
    if (!sessionId || !phaseNumber) return;
    setStatus('saving');
    try {
      // eslint-disable-next-line no-console
      console.log(`[AutoSave phase${phaseNumber}] PUT draft`, { size: JSON.stringify(payload || {}).length });
      const res = await axios.put(
        `${API}/sessions/${sessionId}/drafts/phase${phaseNumber}`,
        payload || {}
      );
      setSavedAt(res.data?.saved_at || new Date().toISOString());
      setStatus('saved');
      // eslint-disable-next-line no-console
      console.log(`[AutoSave phase${phaseNumber}] ✓ saved`);
    } catch (e) {
      setStatus('error');
      // eslint-disable-next-line no-console
      console.error(`[AutoSave phase${phaseNumber}] ✗ save failed`, e?.response?.status, e?.response?.data || e?.message);
    }
  }, [sessionId, phaseNumber]);

  // Debounced auto-save on data change
  useEffect(() => {
    if (!enabled) return;
    // Skip the very first render so we don't overwrite the fresh draft
    // with the empty initial state before loadDraft() has been called.
    if (firstRunRef.current) {
      firstRunRef.current = false;
      lastSerializedRef.current = JSON.stringify(data ?? {});
      return;
    }
    const serialized = JSON.stringify(data ?? {});
    if (serialized === lastSerializedRef.current) {
      // Data reference changed (new object literal from parent) but the
      // *content* is identical — leave any pending save alone. Cancelling
      // the timer here would kill saves triggered by earlier legitimate
      // edits whenever the parent re-renders for an unrelated reason
      // (e.g. a timer tick or an unrelated setState).
      return;
    }
    lastSerializedRef.current = serialized;
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { doSave(data); }, debounceMs);
  }, [data, enabled, doSave, debounceMs]);

  // On unmount, clear any pending debounce timer.
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Flush on unmount / route change / tab close
  useEffect(() => {
    const flush = () => {
      if (!enabled) return;
      const serialized = JSON.stringify(data ?? {});
      if (serialized !== lastSerializedRef.current || status === 'pending') {
        // Best-effort synchronous save on unload
        try {
          const url = `${API}/sessions/${sessionId}/drafts/phase${phaseNumber}`;
          navigator.sendBeacon?.(
            url,
            new Blob([serialized], { type: 'application/json' })
          );
        } catch (e) { /* ignore */ }
      }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [data, enabled, sessionId, phaseNumber, status]);

  const saveNow = useCallback(() => doSave(data), [doSave, data]);

  const loadDraft = useCallback(async () => {
    if (!sessionId || !phaseNumber) return null;
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/drafts/phase${phaseNumber}`);
      if (res.data && res.data.data) {
        setSavedAt(res.data.updated_at || null);
        setStatus('saved');
        return res.data.data;
      }
      return null;
    } catch (e) {
      return null;
    }
  }, [sessionId, phaseNumber]);

  const clearDraft = useCallback(async () => {
    if (!sessionId || !phaseNumber) return;
    try {
      await axios.delete(`${API}/sessions/${sessionId}/drafts/phase${phaseNumber}`);
      setSavedAt(null);
      setStatus('idle');
    } catch (e) { /* ignore */ }
  }, [sessionId, phaseNumber]);

  return { status, savedAt, saveNow, loadDraft, clearDraft };
};

/**
 * AutoSaveIndicator — small text badge showing the current auto-save state.
 * Renders nothing when idle. Pass the status + savedAt from useAutoSave.
 */
export const AutoSaveIndicator = ({ status, savedAt }) => {
  const relTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
      if (s < 5) return 'just now';
      if (s < 60) return `${s}s ago`;
      const m = Math.floor(s / 60);
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      return `${h}h ago`;
    } catch (e) { return ''; }
  };

  const label = (() => {
    switch (status) {
      case 'pending': return 'Unsaved changes…';
      case 'saving':  return 'Saving draft…';
      case 'saved':   return `Draft saved${savedAt ? ' ' + relTime(savedAt) : ''}`;
      case 'error':   return 'Draft save failed';
      default:        return 'Auto-save on';
    }
  })();

  const color = status === 'error'
    ? 'text-red-600'
    : status === 'saved'
      ? 'text-emerald-600'
      : status === 'pending' || status === 'saving'
        ? 'text-blue-600'
        : 'text-gray-500';

  return (
    <span className={`text-xs ${color} inline-flex items-center gap-1.5 px-2 py-1 bg-white/80 border rounded-full`} data-testid="autosave-indicator">
      <span className={`w-2 h-2 rounded-full ${
        status === 'saving' ? 'bg-blue-500 animate-pulse' :
        status === 'saved' ? 'bg-emerald-500' :
        status === 'pending' ? 'bg-amber-500' :
        status === 'error' ? 'bg-red-500' : 'bg-gray-300'
      }`} />
      {label}
    </span>
  );
};

export default useAutoSave;
